'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface AssessmentSection {
  id: string;
  title: string;
  description: string;
  function: string;
  questions: Question[];
}

interface Question {
  id: string;
  text: string;
  category?: string;
  weight: number;
}

interface Answers {
  [key: string]: boolean | null;
}

const sections: AssessmentSection[] = [
  {
    id: 'identify',
    title: 'Identify (ID)',
    description: 'Develop organizational understanding to manage cybersecurity risk',
    function: 'ID',
    questions: [
      { id: 'id1', text: 'Do you maintain an inventory of hardware assets?', category: 'Asset Management', weight: 9 },
      { id: 'id2', text: 'Do you maintain an inventory of software assets?', category: 'Asset Management', weight: 9 },
      { id: 'id3', text: 'Are network communications and data flows mapped?', category: 'Asset Management', weight: 8 },
      { id: 'id4', text: 'Is there a cybersecurity policy approved by leadership?', category: 'Governance', weight: 10 },
      { id: 'id5', text: 'Are legal and regulatory requirements identified?', category: 'Governance', weight: 8 },
      { id: 'id6', text: 'Is there a risk management process?', category: 'Risk Assessment', weight: 10 },
      { id: 'id7', text: 'Are threats and vulnerabilities identified and documented?', category: 'Risk Assessment', weight: 9 },
      { id: 'id8', text: 'Is business impact analysis performed for critical assets?', category: 'Risk Assessment', weight: 8 },
      { id: 'id9', text: 'Is there a supply chain risk management program?', category: 'Supply Chain', weight: 7 },
    ],
  },
  {
    id: 'protect',
    title: 'Protect (PR)',
    description: 'Implement safeguards to ensure delivery of critical services',
    function: 'PR',
    questions: [
      { id: 'pr1', text: 'Are identities and credentials managed?', category: 'Identity Management', weight: 10 },
      { id: 'pr2', text: 'Is physical access to assets controlled?', category: 'Identity Management', weight: 8 },
      { id: 'pr3', text: 'Is remote access managed and secured?', category: 'Identity Management', weight: 9 },
      { id: 'pr4', text: 'Do you provide cybersecurity awareness training?', category: 'Awareness', weight: 9 },
      { id: 'pr5', text: 'Is data at rest protected (encrypted)?', category: 'Data Security', weight: 10 },
      { id: 'pr6', text: 'Is data in transit protected (encrypted)?', category: 'Data Security', weight: 10 },
      { id: 'pr7', text: 'Are assets formally managed throughout removal/transfer?', category: 'Data Security', weight: 7 },
      { id: 'pr8', text: 'Are protective processes continuously improved?', category: 'Processes', weight: 8 },
      { id: 'pr9', text: 'Is technical security architecture documented?', category: 'Technology', weight: 8 },
      { id: 'pr10', text: 'Are systems configured securely (hardening)?', category: 'Technology', weight: 9 },
    ],
  },
  {
    id: 'detect',
    title: 'Detect (DE)',
    description: 'Implement activities to identify cybersecurity events',
    function: 'DE',
    questions: [
      { id: 'de1', text: 'Is there continuous monitoring of network activity?', category: 'Anomalies', weight: 9 },
      { id: 'de2', text: 'Are anomalies and indicators of compromise detected?', category: 'Anomalies', weight: 9 },
      { id: 'de3', text: 'Is there malware detection capability?', category: 'Security Monitoring', weight: 9 },
      { id: 'de4', text: 'Are audit logs reviewed and analyzed?', category: 'Security Monitoring', weight: 9 },
      { id: 'de5', text: 'Is vulnerability scanning performed regularly?', category: 'Security Monitoring', weight: 8 },
      { id: 'de6', text: 'Are detection processes tested and validated?', category: 'Detection Processes', weight: 8 },
      { id: 'de7', text: 'Are event detection roles and responsibilities defined?', category: 'Detection Processes', weight: 7 },
    ],
  },
  {
    id: 'respond',
    title: 'Respond (RS)',
    description: 'Take action regarding detected cybersecurity incidents',
    function: 'RS',
    questions: [
      { id: 'rs1', text: 'Is there an incident response plan?', category: 'Response Planning', weight: 10 },
      { id: 'rs2', text: 'Is the response plan tested and exercised?', category: 'Response Planning', weight: 9 },
      { id: 'rs3', text: 'Are incidents reported to appropriate stakeholders?', category: 'Communications', weight: 8 },
      { id: 'rs4', text: 'Is information shared with external parties as appropriate?', category: 'Communications', weight: 7 },
      { id: 'rs5', text: 'Are incidents analyzed to understand scope and impact?', category: 'Analysis', weight: 9 },
      { id: 'rs6', text: 'Is forensic analysis capability available?', category: 'Analysis', weight: 7 },
      { id: 'rs7', text: 'Are incidents contained to prevent expansion?', category: 'Mitigation', weight: 9 },
      { id: 'rs8', text: 'Are incidents mitigated and resolved?', category: 'Mitigation', weight: 9 },
      { id: 'rs9', text: 'Are lessons learned incorporated into improvements?', category: 'Improvements', weight: 8 },
    ],
  },
  {
    id: 'recover',
    title: 'Recover (RC)',
    description: 'Maintain resilience and restore impaired capabilities',
    function: 'RC',
    questions: [
      { id: 'rc1', text: 'Is there a recovery plan for critical systems?', category: 'Recovery Planning', weight: 10 },
      { id: 'rc2', text: 'Are backups performed and tested?', category: 'Recovery Planning', weight: 10 },
      { id: 'rc3', text: 'Is recovery time objective (RTO) defined?', category: 'Recovery Planning', weight: 8 },
      { id: 'rc4', text: 'Is recovery point objective (RPO) defined?', category: 'Recovery Planning', weight: 8 },
      { id: 'rc5', text: 'Are recovery plans tested regularly?', category: 'Improvements', weight: 9 },
      { id: 'rc6', text: 'Are lessons learned incorporated into recovery plans?', category: 'Improvements', weight: 7 },
      { id: 'rc7', text: 'Are recovery activities coordinated with external parties?', category: 'Communications', weight: 7 },
      { id: 'rc8', text: 'Is public relations managed during recovery?', category: 'Communications', weight: 6 },
    ],
  },
];

export default function NISTCSFPage() {
  const [view, setView] = useState<'landing' | 'assessment' | 'results'>('landing');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [saved, setSaved] = useState(false);

  const {
    enabled: commandCenterEnabled,
    saving,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'nist-csf-assessment',
    toolCategory: 'compliance',
  });

  const handleAnswer = (questionId: string, value: boolean) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const calculateResults = () => {
    let totalWeight = 0;
    let earnedWeight = 0;

    const sectionScores = sections.map((section) => {
      let sectionTotal = 0;
      let sectionEarned = 0;

      section.questions.forEach((q) => {
        sectionTotal += q.weight;
        totalWeight += q.weight;
        if (answers[q.id] === true) {
          sectionEarned += q.weight;
          earnedWeight += q.weight;
        }
      });

      return {
        id: section.id,
        title: section.title,
        function: section.function,
        score: sectionTotal > 0 ? Math.round((sectionEarned / sectionTotal) * 100) : 0,
        gaps: section.questions.filter((q) => answers[q.id] !== true),
      };
    });

    const tier = earnedWeight / totalWeight >= 0.8 ? 4 :
                 earnedWeight / totalWeight >= 0.6 ? 3 :
                 earnedWeight / totalWeight >= 0.4 ? 2 : 1;

    return {
      overallScore: totalWeight > 0 ? Math.round((earnedWeight / totalWeight) * 100) : 0,
      sectionScores,
      tier,
      tierLabel: tier === 4 ? 'Adaptive' : tier === 3 ? 'Repeatable' : tier === 2 ? 'Risk Informed' : 'Partial',
    };
  };

  const results = view === 'results' ? calculateResults() : null;

  const handleSaveToCommandCenter = async () => {
    if (!results || !commandCenterEnabled) return;
    const scores: Record<string, number> = {};
    results.sectionScores.forEach(s => { scores[s.id] = s.score; });
    const gaps = results.sectionScores.flatMap(section =>
      section.gaps.map(gap => ({
        id: gap.id,
        title: gap.text,
        severity: section.score < 40 ? 'critical' as const : section.score < 60 ? 'high' as const : 'medium' as const,
        description: `Gap in ${section.title}`,
      }))
    );
    const assessment = await saveResult({
      name: `NIST CSF Assessment - ${new Date().toLocaleDateString()}`,
      score_overall: results.overallScore,
      scores,
      answers,
      gaps,
    });
    if (assessment) setSaved(true);
  };

  const exportReport = () => {
    if (!results) return;

    const lines = [
      '# NIST Cybersecurity Framework Assessment Report',
      '',
      `**Date:** ${new Date().toLocaleDateString()}`,
      `**Overall Score:** ${results.overallScore}%`,
      `**Implementation Tier:** Tier ${results.tier} - ${results.tierLabel}`,
      '',
      '---',
      '',
      '## Function Scores',
      '',
      ...results.sectionScores.map((s) => `- **${s.title}:** ${s.score}%`),
      '',
      '## Capability Gaps',
      '',
    ];

    results.sectionScores.forEach((section) => {
      if (section.gaps.length > 0) {
        lines.push(`### ${section.title}`);
        lines.push('');
        section.gaps.forEach((gap) => {
          lines.push(`- [ ] ${gap.category ? `[${gap.category}] ` : ''}${gap.text}`);
        });
        lines.push('');
      }
    });

    lines.push('---');
    lines.push('*Generated by The Art of CTO - NIST CSF Assessment Tool*');

    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nist-csf-assessment-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-accent';
    return 'text-red-400';
  };

  const functionColors: Record<string, string> = {
    ID: 'bg-blue-500/20 text-blue-400',
    PR: 'bg-purple-500/20 text-purple-400',
    DE: 'bg-yellow-500/20 text-yellow-400',
    RS: 'bg-red-500/20 text-red-400',
    RC: 'bg-green-500/20 text-green-400',
  };

  return (
    <div className="min-h-screen bg-white dark:bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <nav className="mb-8 text-sm text-light-text-secondary dark:text-foreground-tertiary">
          <Link href="/tools" className="hover:text-accent transition-colors">Tools</Link>
          <span className="mx-2">/</span>
          <span className="text-light-text dark:text-foreground">NIST CSF Assessment</span>
        </nav>

        {view === 'landing' && (
          <>
            <div className="text-center mb-12">
              <div className="inline-block px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium mb-4">
                NIST Cybersecurity Framework 2.0
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-light-text dark:text-foreground mb-4">
                NIST CSF Assessment
              </h1>
              <p className="text-xl text-light-text-secondary dark:text-foreground-secondary max-w-3xl mx-auto">
                Assess your cybersecurity posture against the NIST Cybersecurity Framework&apos;s
                five core functions.
              </p>
            </div>

            <div className="grid grid-cols-5 gap-2 mb-12">
              {[
                { fn: 'ID', name: 'Identify', color: 'bg-blue-500' },
                { fn: 'PR', name: 'Protect', color: 'bg-purple-500' },
                { fn: 'DE', name: 'Detect', color: 'bg-yellow-500' },
                { fn: 'RS', name: 'Respond', color: 'bg-red-500' },
                { fn: 'RC', name: 'Recover', color: 'bg-green-500' },
              ].map((f) => (
                <div key={f.fn} className="text-center">
                  <div className={`${f.color} rounded-lg p-4 mb-2`}>
                    <div className="text-white font-bold text-lg">{f.fn}</div>
                  </div>
                  <div className="text-sm text-light-text dark:text-foreground">{f.name}</div>
                </div>
              ))}
            </div>

            <div className="text-center mb-12">
              <button
                onClick={() => setView('assessment')}
                className="px-8 py-4 bg-accent text-accent-foreground rounded-lg text-lg font-bold hover:bg-accent-hover transition-colors"
              >
                Start Assessment
              </button>
              <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary mt-3">
                40+ controls across 5 core functions
              </p>
            </div>

            <div className="bg-light-surface dark:bg-surface rounded-lg p-8">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">Implementation Tiers</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border-l-4 border-red-500 pl-4">
                  <h3 className="font-semibold text-light-text dark:text-foreground">Tier 1: Partial</h3>
                  <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">Ad-hoc, reactive risk management</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <h3 className="font-semibold text-light-text dark:text-foreground">Tier 2: Risk Informed</h3>
                  <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">Approved but not organization-wide</p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h3 className="font-semibold text-light-text dark:text-foreground">Tier 3: Repeatable</h3>
                  <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">Formal policies, regular updates</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="font-semibold text-light-text dark:text-foreground">Tier 4: Adaptive</h3>
                  <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">Continuous improvement, lessons learned</p>
                </div>
              </div>
            </div>
          </>
        )}

        {view === 'assessment' && (
          <>
            <div className="mb-8">
              <div className="flex justify-between text-sm text-light-text-secondary dark:text-foreground-tertiary mb-2">
                <span>{sections[currentSection].title}</span>
                <span>Function {currentSection + 1} of {sections.length}</span>
              </div>
              <div className="h-2 bg-light-border dark:bg-surface rounded-full overflow-hidden">
                <div className="h-full bg-accent transition-all" style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }} />
              </div>
            </div>

            <div className="bg-light-surface dark:bg-surface rounded-lg p-8 mb-8">
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-3 py-1 rounded text-sm font-bold ${functionColors[sections[currentSection].function]}`}>
                  {sections[currentSection].function}
                </span>
                <h2 className="text-2xl font-bold text-light-text dark:text-foreground">{sections[currentSection].title}</h2>
              </div>
              <p className="text-light-text-secondary dark:text-foreground-secondary mb-8">{sections[currentSection].description}</p>

              <div className="space-y-6">
                {sections[currentSection].questions.map((question) => (
                  <div key={question.id} className="border-b border-light-border dark:border-border-muted pb-6">
                    <div className="flex items-start gap-2 mb-3">
                      {question.category && (
                        <span className="text-xs px-2 py-1 bg-light-border dark:bg-background text-light-text-secondary dark:text-foreground-tertiary rounded">{question.category}</span>
                      )}
                      <p className="text-light-text dark:text-foreground">{question.text}</p>
                    </div>
                    <div className="flex gap-4">
                      <button onClick={() => handleAnswer(question.id, true)} className={`px-6 py-2 rounded-lg font-medium transition-colors ${answers[question.id] === true ? 'bg-green-500 text-white' : 'bg-light-border dark:bg-background text-light-text dark:text-foreground hover:bg-green-500/20'}`}>Yes</button>
                      <button onClick={() => handleAnswer(question.id, false)} className={`px-6 py-2 rounded-lg font-medium transition-colors ${answers[question.id] === false ? 'bg-red-500 text-white' : 'bg-light-border dark:bg-background text-light-text dark:text-foreground hover:bg-red-500/20'}`}>No</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <button onClick={() => currentSection > 0 ? setCurrentSection(currentSection - 1) : setView('landing')} className="px-6 py-3 border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground hover:border-accent transition-colors">{currentSection > 0 ? '← Previous' : '← Back'}</button>
              <button onClick={() => currentSection < sections.length - 1 ? setCurrentSection(currentSection + 1) : setView('results')} className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-medium hover:bg-accent-hover transition-colors">{currentSection < sections.length - 1 ? 'Next Function →' : 'View Results →'}</button>
            </div>
          </>
        )}

        {view === 'results' && results && (
          <>
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-light-text dark:text-foreground mb-4">CSF Assessment Results</h1>
              <div className={`text-6xl font-bold mb-2 ${getScoreColor(results.overallScore)}`}>{results.overallScore}%</div>
              <p className="text-xl text-light-text dark:text-foreground">Tier {results.tier}: {results.tierLabel}</p>
            </div>

            <div className="bg-light-surface dark:bg-surface rounded-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">Function Scores</h2>
              <div className="space-y-4">
                {results.sectionScores.map((section) => (
                  <div key={section.id}>
                    <div className="flex justify-between mb-2">
                      <span className="text-light-text dark:text-foreground flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${functionColors[section.function]}`}>{section.function}</span>
                        {section.title}
                      </span>
                      <span className={getScoreColor(section.score)}>{section.score}%</span>
                    </div>
                    <div className="h-3 bg-light-border dark:bg-background rounded-full overflow-hidden">
                      <div className={`h-full transition-all ${section.score >= 80 ? 'bg-green-500' : section.score >= 60 ? 'bg-yellow-500' : section.score >= 40 ? 'bg-accent' : 'bg-red-500'}`} style={{ width: `${section.score}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-4 justify-center">
              <button onClick={exportReport} className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-medium hover:bg-accent-hover transition-colors">Export Report</button>
              {commandCenterEnabled && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving || saved}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : saved ? 'Saved to Command Center' : 'Save to Command Center'}
                </button>
              )}
              <button onClick={() => { setView('landing'); setCurrentSection(0); setAnswers({}); setSaved(false); }} className="px-6 py-3 border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground hover:border-accent transition-colors">Start Over</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
